import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { catchError, interval, of, startWith, switchMap } from 'rxjs';

import { AlertasLeadsService, MisAlertasLeadsResponse } from './alertas-leads.service';

@Component({
  selector: 'inv-alertas-leads',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    RouterModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  styles: [
    `
      :host { display: inline-flex; }

      /* mat-menu trae max-width: 280px y recortaría el panel. La clase va en el
         <mat-menu> y Material la copia al panel del overlay, que vive fuera del
         DOM del componente: por eso ::ng-deep. */
      ::ng-deep .alertas-leads-panel-wrapper.mat-mdc-menu-panel {
        max-width: none;
      }
      .alertas-panel {
        width: 380px;
        max-width: calc(100vw - 2rem);
        max-height: 70vh;
        overflow-y: auto;
      }
      .seccion-titulo {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    `,
  ],
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="menu"
      [matTooltip]="
        alertas?.total
          ? 'Tienes ' + alertas?.total + ' leads para atender'
          : 'Sin leads pendientes'
      "
      class="!text-default"
    >
      <mat-icon
        *ngIf="(alertas?.vencidos?.length ?? 0) > 0; else iconoSinVencidos"
        [matBadge]="alertas?.total"
        matBadgeColor="warn"
        matBadgeSize="small"
        class="text-red-600"
        >person_alert</mat-icon
      >
      <ng-template #iconoSinVencidos>
        <mat-icon
          [matBadge]="(alertas?.total ?? 0) > 0 ? alertas?.total : null"
          matBadgeColor="accent"
          matBadgeSize="small"
          >how_to_reg</mat-icon
        >
      </ng-template>
    </button>

    <mat-menu #menu="matMenu" class="alertas-leads-panel-wrapper" xPosition="before">
      <div class="alertas-panel" (click)="$event.stopPropagation()">
        <div class="px-4 pt-3 pb-2 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div class="flex items-center justify-between">
            <div class="font-semibold text-gray-900 flex items-center gap-2">
              <mat-icon class="!text-base !w-5 !h-5">how_to_reg</mat-icon>
              Mis leads
              <!-- De qué empresa es este aviso. Andrés reportó que las
                   notificaciones de Invering e Infinitus se mezclaban sin
                   distinguirse; los leads son de Infinitus y hay que decirlo. -->
              <span class="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-50 text-teal-800 border border-teal-200">
                Infinitus
              </span>
            </div>
            <button
              mat-icon-button
              (click)="cargar(); $event.stopPropagation()"
              matTooltip="Recargar"
              class="!w-8 !h-8"
            >
              <mat-icon class="!text-base !w-5 !h-5">refresh</mat-icon>
            </button>
          </div>
          <div *ngIf="alertas" class="text-xs text-secondary mt-1">
            {{ alertas.total }} para atender ·
            <span class="text-red-700 font-semibold">{{ alertas.vencidos.length }} vencidos</span>
            · <span class="text-amber-700">{{ alertas.porVencer.length }} por vencer</span>
            · <span class="text-blue-700">{{ alertas.sinAsignar.length }} sin asignar</span>
            <span *ngIf="alertas.sinGestionar?.length"
              > · <span class="text-slate-700">{{ alertas.sinGestionar.length }} sin gestionar</span></span
            >
            <span *ngIf="alertas.visitasSinRegistrar?.length"
              > · <span class="text-teal-700">{{ alertas.visitasSinRegistrar.length }} visitas sin registrar</span></span
            >
          </div>

          <!-- Si el sondeo falla, se dice. Sin esto la campana se queda con lo
               último que supo y se ve idéntica a una al día — que es cómo un corte
               de red terminaba pareciendo «no tienes nada pendiente». -->
          <div
            *ngIf="desconectado"
            class="mt-2 text-xs rounded bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1">
            Sin conexión con el servidor: esto puede estar desactualizado.
          </div>
        </div>

        <!-- Vencidos: pasó el plazo de primer contacto -->
        <ng-container *ngIf="(alertas?.vencidos?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-red-700 font-bold">
            ⏰ Sin contactar — plazo vencido
          </div>
          <a
            *ngFor="let l of alertas?.vencidos"
            mat-menu-item
            [routerLink]="['/apps/infinitus/leads']"
            [queryParams]="{ lead: l.id }"
            class="!h-auto !py-3"
          >
            <div class="flex flex-col gap-0.5 w-full">
              <div class="flex items-start gap-2">
                <mat-icon class="text-red-600 !text-base !w-5 !h-5 flex-shrink-0"
                  >schedule</mat-icon
                >
                <span class="text-sm font-medium whitespace-normal leading-tight">
                  {{ l.nombre }}
                </span>
              </div>
              <div class="text-[11px] text-red-700 pl-7">
                {{ etiquetaAtraso(l.horas) }}
                <span *ngIf="l.clasificacion"> · {{ l.clasificacion }}</span>
                <span *ngIf="l.zona"> · {{ l.zona }}</span>
              </div>
              <div *ngIf="l.telefono" class="text-[11px] text-secondary pl-7">
                {{ l.telefono }}
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Por vencer: el plazo se cumple dentro de 24 h -->
        <ng-container *ngIf="(alertas?.porVencer?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-amber-700 font-bold">
            🕑 Por vencer (24 h)
          </div>
          <a
            *ngFor="let l of alertas?.porVencer"
            mat-menu-item
            [routerLink]="['/apps/infinitus/leads']"
            [queryParams]="{ lead: l.id }"
            class="!h-auto !py-2"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm whitespace-normal leading-tight">{{ l.nombre }}</span>
              <div class="text-[11px] text-secondary">
                {{ etiquetaRestante(l.horas) }}
                <span *ngIf="l.clasificacion"> · {{ l.clasificacion }}</span>
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Sin asignar: leads calientes que no tienen dueño -->
        <ng-container *ngIf="(alertas?.sinAsignar?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-blue-700 font-bold">
            👤 Calientes sin asignar
          </div>
          <a
            *ngFor="let l of alertas?.sinAsignar"
            mat-menu-item
            [routerLink]="['/apps/infinitus/leads']"
            [queryParams]="{ lead: l.id }"
            class="!h-auto !py-2"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm whitespace-normal leading-tight">{{ l.nombre }}</span>
              <div class="text-[11px] text-secondary">
                nadie lo tiene asignado
                <span *ngIf="l.zona"> · {{ l.zona }}</span>
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Sin gestionar: míos, sin contactar y sin cadencia corriendo.
             Antes se caían de las tres listas y desaparecían con dueño puesto. -->
        <ng-container *ngIf="(alertas?.sinGestionar?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-slate-700 font-bold">
            📋 Míos, sin gestionar
          </div>
          <a
            *ngFor="let l of alertas?.sinGestionar"
            mat-menu-item
            [routerLink]="['/apps/infinitus/leads']"
            [queryParams]="{ lead: l.id }"
            class="!h-auto !py-2"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm whitespace-normal leading-tight">{{ l.nombre }}</span>
              <div class="text-[11px] text-secondary">
                sin primer contacto
                <span *ngIf="l.zona"> · {{ l.zona }}</span>
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Visitas sin registrar: visitaron, o la cita ya pasó, y nadie anotó
             qué ocurrió. El registro es opcional a propósito, así que el
             faltante se publica acá o se pierde en silencio. -->
        <ng-container *ngIf="(alertas?.visitasSinRegistrar?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-teal-700 font-bold">
            🚪 Visitas sin registrar
          </div>
          <a
            *ngFor="let l of alertas?.visitasSinRegistrar"
            mat-menu-item
            [routerLink]="['/apps/infinitus/leads']"
            [queryParams]="{ lead: l.id }"
            class="!h-auto !py-2"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm whitespace-normal leading-tight">{{ l.nombre }}</span>
              <div class="text-[11px] text-secondary">
                {{ l.estado === 'cita_agendada' ? 'la cita ya pasó' : 'sin registrar qué pasó' }}
                <span *ngIf="l.zona"> · {{ l.zona }}</span>
              </div>
            </div>
          </a>
        </ng-container>

        <div
          *ngIf="alertas && alertas.total === 0"
          class="text-center py-10 text-secondary"
        >
          <mat-icon class="!text-5xl !w-12 !h-12 text-gray-300 mb-2">check_circle</mat-icon>
          <p class="text-sm">Todo al día — ningún lead esperando.</p>
        </div>

        <div
          *ngIf="alertas && alertas.total > 0"
          class="px-4 py-3 border-t border-gray-200 sticky bottom-0 bg-white"
        >
          <a mat-stroked-button class="!w-full" routerLink="/apps/infinitus/leads">
            Ver todos los leads
          </a>
        </div>
      </div>
    </mat-menu>
  `,
})
export class AlertasLeadsComponent implements OnInit {
  private readonly service = inject(AlertasLeadsService);
  private readonly destroyRef = inject(DestroyRef);

  alertas: MisAlertasLeadsResponse | null = null;

  /** true si el último sondeo falló. Lo que se ve puede estar viejo. */
  desconectado = false;

  ngOnInit(): void {
    // Cada 2 minutos. Los plazos de leads se miden en horas, así que refrescar
    // más seguido no aportaría nada y sí sumaría consultas.
    interval(120_000)
      .pipe(
        startWith(0),
        // `catchError` va DENTRO del switchMap, no fuera.
        //
        // Si el error del observable interno sube al externo, RxJS termina el
        // stream: el `error` handler de abajo lo silenciaba pero el interval
        // quedaba muerto para siempre. Un corte de red de cinco segundos dejaba
        // la campana sin volver a sondear en toda la sesión — y sin badge, que se
        // lee como «no hay nada pendiente». Devolver `null` acá mantiene vivo el
        // ciclo y marca la desconexión sin borrar lo último que sí se supo.
        switchMap(() => this.service.misAlertas().pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (a) => {
          if (a === null) {
            // NO se borra `alertas`: mostrar cero sería indistinguible de «no hay
            // pendientes», que es lo contrario de lo que está pasando.
            this.desconectado = true;
            return;
          }
          this.desconectado = false;
          this.alertas = a;
        },
      });
  }

  cargar(): void {
    this.service.misAlertas().subscribe({
      next: (a) => (this.alertas = a),
      error: () => undefined,
    });
  }

  /** "vencido hace 3 h" se entiende mejor que una fecha, que obliga a restar. */
  etiquetaAtraso(horas?: number | null): string {
    if (horas == null) return 'plazo vencido';
    if (horas < 1) return 'vencido hace menos de 1 h';
    if (horas < 24) return `vencido hace ${horas} h`;
    const dias = Math.floor(horas / 24);
    return `vencido hace ${dias} ${dias === 1 ? 'día' : 'días'}`;
  }

  etiquetaRestante(horas?: number | null): string {
    if (horas == null) return 'sin plazo';
    if (horas < 1) return 'vence en menos de 1 h';
    return `vence en ${horas} h`;
  }
}
